import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-lite';
import { Box, Typography } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { EventThemesStore, EventTheme } from '../Stores/EventThemesStore';
import { speakMessage } from '../Utils/speakMessage';

interface EventOverlayProps {
    eventThemesStore: EventThemesStore;
    eventClassification: string | null;
    isVisible: boolean;
    onOverlayActiveChanged?: (active: boolean) => void;
}

// CSS animations for pulsating effect
const pulseKeyframes = `
@keyframes pulse-glow {
    0%, 100% {
        box-shadow: 
            0 0 10px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.2),
            0 0 20px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.1),
            0 4px 16px rgba(0, 0, 0, 0.5),
            inset 0 0 10px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.04);
    }
    50% {
        box-shadow: 
            0 0 15px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.3),
            0 0 30px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.15),
            0 6px 20px rgba(0, 0, 0, 0.6),
            inset 0 0 15px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.06);
    }
}

@keyframes pulse-border {
    0%, 100% {
        border-color: rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.35);
        box-shadow: 
            inset 0 0 15px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.04),
            0 0 8px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.15);
    }
    50% {
        border-color: rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.5);
        box-shadow: 
            inset 0 0 20px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.06),
            0 0 12px rgba(var(--theme-r), var(--theme-g), var(--theme-b), 0.2);
    }
}
`;


export const EventOverlay: React.FC<EventOverlayProps> = observer(({ eventThemesStore, eventClassification, isVisible, onOverlayActiveChanged }) => {
    const theme = eventThemesStore.getThemeByClassification(eventClassification as string);

    // Remember the last valid theme so we can still render during exit animation
    const lastThemeRef = useRef<{ theme: EventTheme; themeRgb: string; borderColor: string } | null>(null);
    if (theme) {
        const { r, g, b } = theme.color;
        lastThemeRef.current = {
            theme,
            themeRgb: `${r}, ${g}, ${b}`,
            borderColor: eventThemesStore.getColorStringWithAlpha(theme, 0.2),
        };
    }

    useEffect(() => {
        if ('fully' in window) {
            const thisWindow = window as any;

            try {
                thisWindow.fully.textToSpeech.initTts();
            } catch {
                // Ignore errors
            }
        }
    }, []);

   const messageToSpeak = theme?.speechMessage;

    useEffect(() => {
        if (!messageToSpeak) {
            return;
        }

        speakMessage(messageToSpeak);
    }, [messageToSpeak]);

    const shouldShow = isVisible && !!eventClassification && !!theme;

    // Notify parent when overlay becomes active
    const prevShouldShowRef = useRef(false);
    useEffect(() => {
        if (shouldShow && !prevShouldShowRef.current) {
            onOverlayActiveChanged?.(true);
        }
        prevShouldShowRef.current = shouldShow;
    }, [shouldShow, onOverlayActiveChanged]);

    const displayTheme = theme ?? lastThemeRef.current?.theme;
    const r = displayTheme?.color.r ?? 0;
    const g = displayTheme?.color.g ?? 0;
    const b = displayTheme?.color.b ?? 0;
    const a = displayTheme?.color.a ?? 0;
    const themeRgb = lastThemeRef.current?.themeRgb ?? `${r}, ${g}, ${b}`;
    const borderColor = lastThemeRef.current?.borderColor ?? '';

    return (
        <>
            {/* Inject CSS animations */}
            <style>{pulseKeyframes}</style>

            <AnimatePresence onExitComplete={() => onOverlayActiveChanged?.(false)}>
                {shouldShow && (
                    <motion.div
                        key="event-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6 }}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            pointerEvents: 'none',
                            zIndex: 1000,
                            '--theme-r': r.toString(),
                            '--theme-g': g.toString(),
                            '--theme-b': b.toString(),
                        } as any}
                    >
                        {/* Message chip */}
                        <motion.div
                            initial={{
                                top: '-10%',
                                left: '50%',
                                x: '-50%',
                                y: '-50%',
                                scale: 0.5,
                                opacity: 0,
                            }}
                            animate={{
                                top: ['-50%', '50%', '50%', '50%', '50%'],
                                scale: [0.5, 1, 3.6, 2.8, 3],
                                opacity: [0, 1, 1, 1, 1],
                            }}
                            exit={{
                                top: '-50%',
                                scale: 0.5,
                                opacity: 0,
                            }}
                            transition={{
                                duration: 0.6,
                                ease: 'easeInOut',
                            }}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1001,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                paddingLeft: 20,
                                paddingRight: 20,
                                paddingTop: 8,
                                paddingBottom: 8,
                                borderRadius: 40,
                                backdropFilter: 'blur(16px)',
                                background: 'rgba(0,0,0,0.6)',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.08)',
                                border: `2px solid rgba(${themeRgb}, 0.4)`,
                                animation: 'pulse-glow 2s ease-in-out infinite',
                            }}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                sx={{ color: '#fff', whiteSpace: 'nowrap' }}
                            >
                                {displayTheme!.message}
                            </Typography>
                        </motion.div>

                        {/* Full area border overlay */}
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                border: `3px solid ${borderColor}`,
                                borderRadius: 2,
                                pointerEvents: 'none',
                                animation: 'pulse-border 2s ease-in-out infinite',
                                background: `rgba(${themeRgb}, ${a})`,
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
});
